import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useApi, configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/** Domain status of a tracked infrastructure workflow (mirror of the backend). */
type InfraWorkflowStatus =
  | 'RUNNING'
  | 'FINISHED'
  | 'FAILED'
  | 'TIMEOUT'
  | 'DESTROYED';

/**
 * Subset of an `autom2plataforma` workflow this component needs, as returned by
 * `GET /api/autom2plataforma/workflows`. Modelled locally so the app only
 * depends on the plugin's public HTTP contract.
 */
interface InfraWorkflowItem {
  contextId: string;
  sourceId: string;
  workflowId: string;
  apm?: string;
  templateRef?: string;
  environment: string;
  hostname?: string;
  status: InfraWorkflowStatus;
  infrastructure?: Record<string, unknown>;
  createdAt: string;
}

/** Result handed back to the parent when a FINISHED infra is selected. The
 * raw infrastructure outputs are intentionally NOT part of the selection: the
 * CD configuration is built server-side by the `from-infra` endpoint from the
 * workflowId, so outputs never travel through the browser. */
export interface InfraCdSelection {
  workflowId: string;
  /**
   * CD template spec declared by the golden path's `acciona.io/cd-template`
   * annotation, verbatim ("<name>:<version>"), if any. The backend's
   * from-infra endpoint resolves it the same way — callers must split it
   * themselves rather than treating it as a bare template name.
   */
  cdTemplate?: string;
  /** CD environment (des/int/sta/pre/pro) mapped from the infra environment. */
  environment?: string;
  infra: InfraWorkflowItem;
}

interface InfraCdSelectorProps {
  /** Business application APM (APMXXXXXXX) of the repository. */
  apm: string;
  /**
   * CD environment whose "+" was clicked (des/int/sta/pre/pro). When set, only
   * infras whose IaC environment maps to it are shown (Development → DEV,
   * Preproducción → PRE, Producción → PRO). When null/undefined (the generic
   * add button), every infra of the APM is shown.
   */
  targetCdEnv?: string | null;
  /** Currently selected infra (controlled highlight), or null. */
  selectedWorkflowId?: string | null;
  /**
   * May return a Promise: `handleSelect` awaits it so this item's inline
   * spinner stays visible for the parent's whole async resolution instead of
   * disappearing before the parent has anything to show.
   */
  onSelect: (selection: InfraCdSelection) => void | Promise<void>;
  /**
   * Reports, once the infra list has loaded (and after each `targetCdEnv`
   * filtering), whether there is at least one infra to show for this
   * apm/environment. `CdConfigurationPanel` uses this to switch straight to
   * the manual flow the first time it finds there is nothing to select,
   * instead of making the user notice the empty list and click the fallback
   * link themselves.
   */
  onAvailabilityChange?: (hasInfra: boolean) => void;
}

/** Annotation on the golden-path Template declaring its CD template. */
const CD_TEMPLATE_ANNOTATION = 'acciona.io/cd-template';

/**
 * Maps the IaC environment (DEV/PRE/PRO, as launched by the golden path) to the
 * CD environment taxonomy used by the config editor (des/int/sta/pre/pro).
 * DEV→des, PRE→pre, PRO→pro; anything else is left unmapped (manual choice).
 */
function mapInfraEnvToCdEnv(environment: string): string | undefined {
  switch (environment?.toUpperCase()) {
    case 'DEV':
      return 'des';
    case 'PRE':
      return 'pre';
    case 'PRO':
      return 'pro';
    default:
      return undefined;
  }
}

const STATUS_LABELS: Record<InfraWorkflowStatus, string> = {
  RUNNING: 'En curso',
  FINISHED: 'Finalizada',
  FAILED: 'Fallida',
  TIMEOUT: 'Caducada',
  DESTROYED: 'Destruida',
};

function statusChipColor(
  status: InfraWorkflowStatus,
): 'primary' | 'secondary' | 'default' {
  if (status === 'FINISHED') return 'primary';
  if (status === 'FAILED' || status === 'TIMEOUT') return 'secondary';
  return 'default';
}

/**
 * Lists the infrastructures provisioned for a repository's APM via the
 * "Creación de IaC MVP" golden path. Every infra is shown (RUNNING/FAILED are
 * displayed disabled) but only `FINISHED` ones can be selected. Selecting one
 * resolves the CD template declared by the golden path that created it and
 * hands it, together with the mapped environment, back to the parent, which
 * builds the CD configuration entirely server-side from the infra outputs.
 * The caller (`CdConfigurationPanel`) also offers a manual-configuration
 * fallback alongside this selector — see that component for how both modes
 * coexist.
 */
export const InfraCdSelector = ({
  apm,
  targetCdEnv,
  selectedWorkflowId,
  onSelect,
  onAvailabilityChange,
}: InfraCdSelectorProps) => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const [items, setItems] = useState<InfraWorkflowItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvingWorkflowId, setResolvingWorkflowId] = useState<
    string | null
  >(null);

  useEffect(() => {
    let cancelled = false;
    const fetchInfras = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = (await identityApi.getCredentials()).token;
        const url = `${backendUrl}/api/autom2plataforma/workflows?apm=${encodeURIComponent(
          apm,
        )}`;
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        if (!cancelled) {
          setItems(Array.isArray(data.items) ? data.items : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(`No se pudieron cargar las infraestructuras: ${err}`);
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchInfras();
    return () => {
      cancelled = true;
    };
  }, [apm, backendUrl, identityApi]);

  /** Resolves the CD template annotation from the golden-path template ref. */
  const resolveCdTemplate = async (
    templateRef?: string,
  ): Promise<string | undefined> => {
    if (!templateRef) return undefined;
    try {
      const template = await catalogApi.getEntityByRef(templateRef);
      const value =
        template?.metadata.annotations?.[CD_TEMPLATE_ANNOTATION];
      return value && value.length > 0 ? value : undefined;
    } catch {
      return undefined;
    }
  };

  const handleSelect = async (infra: InfraWorkflowItem) => {
    if (infra.status !== 'FINISHED') return;
    setResolvingWorkflowId(infra.workflowId);
    try {
      const cdTemplate = await resolveCdTemplate(infra.templateRef);
      await onSelect({
        workflowId: infra.workflowId,
        cdTemplate,
        environment: mapInfraEnvToCdEnv(infra.environment),
        infra,
      });
    } finally {
      setResolvingWorkflowId(null);
    }
  };

  // Reports availability once the list has settled (not on error: a fetch
  // failure is not "no infra", so it must not trigger the parent's
  // auto-switch-to-manual behaviour).
  useEffect(() => {
    if (loading || error || !items) return;
    const visible = targetCdEnv
      ? items.filter(infra => mapInfraEnvToCdEnv(infra.environment) === targetCdEnv)
      : items;
    onAvailabilityChange?.(visible.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, items, targetCdEnv]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: 8, padding: 8 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Cargando infraestructuras…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" style={{ marginBottom: 8 }}>
        {error}
      </Alert>
    );
  }

  // When an environment's "+" was clicked, only show infras of that same CD
  // environment (DEV→des, PRE→pre, PRO→pro). The generic add button (no
  // targetCdEnv) shows every infra of the APM.
  const visibleItems = targetCdEnv
    ? (items ?? []).filter(
        infra => mapInfraEnvToCdEnv(infra.environment) === targetCdEnv,
      )
    : items ?? [];

  if (visibleItems.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary" style={{ padding: 8 }}>
        {targetCdEnv
          ? `No hay infraestructuras de IaC para este entorno (${targetCdEnv}) en este APM. Lanza el golden path "Creación de IaC MVP" para aprovisionar la infraestructura antes de configurar el CD.`
          : 'No hay infraestructuras creadas con el golden path de IaC para este APM. Lanza el golden path "Creación de IaC MVP" para aprovisionar la infraestructura antes de configurar el CD.'}
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
        Infraestructura aprovisionada (Golden Path IaC)
      </Typography>
      <Typography variant="caption" color="textSecondary">
        Selecciona una infraestructura <b>Finalizada</b> para configurar el CD
        automáticamente a partir de sus datos.
      </Typography>
      <List dense>
        {visibleItems.map(infra => {
          const selectable = infra.status === 'FINISHED';
          const isSelected = selectedWorkflowId === infra.workflowId;
          const isResolving = resolvingWorkflowId === infra.workflowId;
          const primary = infra.hostname || infra.workflowId;
          const cdEnv = mapInfraEnvToCdEnv(infra.environment);
          const row = (
            <ListItem
              key={infra.workflowId}
              button
              disabled={!selectable}
              selected={isSelected}
              onClick={selectable ? () => handleSelect(infra) : undefined}
            >
              <ListItemIcon>
                {isResolving ? (
                  <CircularProgress size={20} />
                ) : (
                  <Radio
                    edge="start"
                    checked={isSelected}
                    disabled={!selectable}
                    tabIndex={-1}
                    disableRipple
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary={primary}
                secondary={`Entorno: ${infra.environment}${
                  cdEnv ? ` → ${cdEnv}` : ''
                }`}
              />
              <Chip
                size="small"
                label={STATUS_LABELS[infra.status] ?? infra.status}
                color={statusChipColor(infra.status)}
              />
            </ListItem>
          );
          return selectable ? (
            row
          ) : (
            <Tooltip
              key={infra.workflowId}
              title="Solo las infraestructuras finalizadas pueden usarse para configurar el CD"
            >
              <span>{row}</span>
            </Tooltip>
          );
        })}
      </List>
    </Box>
  );
};
